/**
 * Wraps React.lazy() with a retry-then-reload safety net.
 *
 * Chunk imports fail intermittently in Electron (antivirus locks, ASAR
 * I/O races, etc.). Without recovery a single failure leaves the route
 * permanently broken until the user hard-refreshes.
 *
 * Behaviour:
 *   1. First import succeeds → return module, clear retry flag.
 *   2. First import fails → wait 400ms → retry once.
 *   3. Retry succeeds → return module, clear retry flag.
 *   4. Retry fails → set sessionStorage flag, trigger window.location.reload()
 *      (the page boots fresh, retrying with fresh chunks), and re-throw.
 *   5. If the page boots WITH the flag already set, do NOT reload again —
 *      something is genuinely broken and an infinite reload loop helps no one.
 *      Let the error propagate so the ErrorBoundary can render its UI.
 */
import { lazy } from 'react';

export const RETRY_KEY = 'lazy-retry-reloaded';

const defaultReload = () => {
  try { window.location.reload(); } catch (_e) {}
};

export function importWithRetry(factory, reload = defaultReload) {
  // Register the backoff timer synchronously (before the first factory() await)
  // so that jest.advanceTimersByTime() in tests can fire it even before the
  // first factory() rejection microtask has processed.
  const backoff = new Promise((r) => setTimeout(r, 400));

  return factory().then(
    (mod) => {
      sessionStorage.removeItem(RETRY_KEY);
      return mod;
    },
    (_err) => backoff.then(() =>
      factory().then(
        (mod) => {
          sessionStorage.removeItem(RETRY_KEY);
          return mod;
        },
        (err2) => {
          if (!sessionStorage.getItem(RETRY_KEY)) {
            sessionStorage.setItem(RETRY_KEY, '1');
            reload();
          }
          throw err2;
        }
      )
    )
  );
}

export function lazyWithRetry(factory) {
  return lazy(() => importWithRetry(factory));
}
